import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, X, ArrowRight, BookOpen, Calendar, Utensils, ShoppingCart, Droplets, ChevronDown, ShieldCheck, Clock } from 'lucide-react';
// Reusable animation wrapper
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const PrimaryButton = ({
  children,
  onClick,
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      import('react-facebook-pixel').then(mod => {
        const ReactPixel = mod.default;
        ReactPixel.track('AddToCart');
      });
      window.open('https://www.puntovital.lat/cart/45428226818150:1', '_blank');
    }
  };
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={handleClick} className={`relative group overflow-hidden bg-gold-500 hover:bg-gold-600 text-white font-bold text-lg md:text-xl py-4 px-8 rounded-full shadow-lg transition-all duration-300 w-full md:w-auto ${className}`} data-id="element-1">
      <div className="absolute inset-0 w-full h-full bg-white/20 group-hover:animate-[shimmer_1.5s_infinite] -translate-x-full group-hover:translate-x-full transition-transform skew-x-12" data-id="element-2"></div>
      <span className="relative flex items-center justify-center gap-2" data-id="element-3">
        {children}
        <ArrowRight className="w-6 h-6" data-id="element-4" />
      </span>
    </motion.button>;
};
export function App() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream-50 selection:bg-forest-800 selection:text-white" data-id="element-5">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 px-6 overflow-hidden bg-cream-100" data-id="element-6">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-7">
          <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-forest-800/5 blur-3xl" data-id="element-8"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-gold-500/10 blur-3xl" data-id="element-9"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-10">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-11">
            <FadeIn data-id="element-12">
              <div className="inline-block mb-4 px-4 py-1.5 bg-forest-800/10 text-forest-900 rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-13">
                Nuevo Lanzamiento
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-14">
                Desinflama tu abdomen en{' '}
                <span className="text-forest-800 italic" data-id="element-15">7 días</span> sin dejar
                de comer lo que amas
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8" data-id="element-16">
                El método científico que ya usan +2,500 mujeres.
              </p>
              <div className="animate-pulse-slow" data-id="element-17">
                <PrimaryButton onClick={scrollToOffer} data-id="element-18">
                  ¡QUIERO DESINFLAMARME AHORA!
                </PrimaryButton>
              </div>
              <div className="mt-6 flex items-center gap-4 text-sm text-gray-600 font-medium" data-id="element-19">
                <div className="flex -space-x-2" data-id="element-20">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-300 border-2 border-cream-100 flex items-center justify-center overflow-hidden" data-id="element-21">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-22" />
                    </div>)}
                </div>
                <p data-id="element-23">+2,500 mujeres ya lo probaron</p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="relative" data-id="element-24">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/50" data-id="element-25">
                <img src="https://cdn.magicpatterns.com/uploads/9BAHzn6bZatk6jPkfLupzN/heylloyd-image-1775614614072.png" alt="Protocolo Adiós Glucosa Incógnita en dispositivos" className="w-full h-auto object-cover" data-id="element-26" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-27"></div>
              </div>
              {/* Floating badge */}
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-3" data-id="element-28">
                <div className="bg-green-100 p-2 rounded-full" data-id="element-29">
                  <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <p className="text-xs text-gray-500 font-medium" data-id="element-32">
                    Resultados en
                  </p>
                  <p className="font-bold text-forest-900" data-id="element-33">Solo 7 Días</p>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-16 px-6 bg-cream-100" data-id="element-34">
        <div className="max-w-5xl mx-auto" data-id="element-35">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-36">
            <FadeIn data-id="element-37">
              <div className="aspect-square rounded-full bg-white shadow-2xl flex items-center justify-center p-12 relative" data-id="element-38">
                <div className="absolute inset-0 border-2 border-dashed border-gold-500 rounded-full animate-[spin_60s_linear_infinite]" data-id="element-39"></div>
                <div className="text-center" data-id="element-40">
                  <h3 className="text-2xl font-bold text-forest-900 mb-2" data-id="element-41">
                    Método V-P-C
                  </h3>
                  <p className="text-sm text-gray-500 uppercase tracking-widest mb-6" data-id="element-42">
                    El Orden Científico
                  </p>
                  <div className="flex flex-col gap-4" data-id="element-43">
                    <div className="bg-green-100 text-green-800 py-2 px-6 rounded-full font-medium" data-id="element-44">
                      1. Verdes (Fibra)
                    </div>
                    <div className="bg-red-100 text-red-800 py-2 px-6 rounded-full font-medium" data-id="element-45">
                      2. Proteína & Grasa
                    </div>
                    <div className="bg-amber-100 text-amber-800 py-2 px-6 rounded-full font-medium" data-id="element-46">
                      3. Carbohidratos
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            <div data-id="element-47">
              <FadeIn data-id="element-48">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-49">
                  El secreto:{' '}
                  <span className="text-forest-800" data-id="element-50">el orden en que comes</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6" data-id="element-51">
                  Crea un filtro natural en tu intestino que frena los picos de
                  glucosa. Sin dietas, sin restricciones.
                </p>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100" data-id="element-52">
                  <p className="text-lg font-medium text-forest-900 italic" data-id="element-53">
                    "Come tu tortilla y tu postre favorito — tu cuerpo lo
                    procesará sin inflamarse."
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 px-6 bg-white" data-id="element-54">
        <div className="max-w-4xl mx-auto" data-id="element-55">
          <FadeIn data-id="element-56">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10" data-id="element-57">
              Tu acceso incluye
            </h2>
          </FadeIn>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-58">
            {[{
            icon: BookOpen,
            title: 'Protocolo Maestro (eBook)'
          }, {
            icon: Calendar,
            title: 'Plan de 7 Días con menú'
          }, {
            icon: Utensils,
            title: 'Guía para restaurantes'
          }, {
            icon: ShoppingCart,
            title: 'Lista de compras Anti-Picos'
          }, {
            icon: Droplets,
            title: 'Bono: Hack del Vinagre'
          }, {
            icon: ShieldCheck,
            title: 'Acceso inmediato'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-59">
                <div className="flex flex-col items-center text-center p-5 bg-cream-50 rounded-2xl border border-cream-100" data-id="element-60">
                  <div className="bg-forest-900 p-3 rounded-xl text-gold-500 mb-3" data-id="element-61">
                    <item.icon className="w-6 h-6" data-id="element-62" />
                  </div>
                  <p className="font-semibold text-forest-900 text-sm" data-id="element-63">
                    {item.title}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-cream-100" data-id="element-64">
        <div className="max-w-3xl mx-auto" data-id="element-65">
          <FadeIn data-id="element-66">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10" data-id="element-67">
              Preguntas Frecuentes
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-68">
            {[{
            q: '¿Tengo que dejar de comer tortillas?',
            a: 'No, solo cambias el orden en que las comes.'
          }, {
            q: '¿Es difícil de seguir?',
            a: 'Solo cambias el orden de lo que ya tienes en tu plato.'
          }, {
            q: '¿En cuánto tiempo veo resultados?',
            a: 'La mayoría nota cambios desde el tercer día.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-69">
                <details className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-id="element-70">
                  <summary className="flex items-center justify-between p-6 cursor-pointer font-bold text-lg text-forest-900" data-id="element-71">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" data-id="element-72" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-73">
                    {faq.a}
                  </div>
                </details>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section className="py-24 px-6 bg-forest-900 text-white relative overflow-hidden" data-id="element-74">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-75">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-full max-h-[800px] rounded-full bg-gold-500/10 blur-[100px]" data-id="element-76"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-77">
          <FadeIn data-id="element-78">
            <div className="bg-white text-dark rounded-3xl p-8 md:p-12 shadow-2xl text-center max-w-2xl mx-auto relative overflow-hidden" data-id="element-79">
              {/* Discount ribbon */}
              <div className="absolute -right-12 top-8 rotate-45 bg-red-500 text-white text-sm font-bold py-1.5 px-12 shadow-md z-20" data-id="element-80">
                -70% OFF
              </div>

              <h2 id="offer-section" className="text-3xl md:text-4xl font-bold mb-4 text-forest-900 scroll-mt-6" data-id="element-81">
                Comienza tu transformación hoy
              </h2>
              <p className="text-lg text-gray-600 mb-4" data-id="element-82">
                Obtén el protocolo completo y todos los bonos por un pago único
                de lanzamiento. Recupera tu bienestar hoy mismo por el precio de
                una comida corrida.
              </p>

              {/* Urgency badge */}
              <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 font-semibold text-sm px-4 py-2 rounded-full mb-8" data-id="element-83">
                <Clock className="w-4 h-4" data-id="element-84" />
                Oferta por tiempo limitado — ¡Últimas horas!
              </motion.div>

              <div className="mb-8" data-id="element-85">
                <p className="text-sm text-gray-400 uppercase tracking-widest font-bold mb-3" data-id="element-86">
                  Precio Especial de Lanzamiento
                </p>
                <div className="flex items-center justify-center gap-3 mb-2" data-id="element-87">
                  <span className="text-2xl md:text-3xl text-gray-400 line-through font-medium" data-id="element-88">
                    $997
                  </span>
                  <span className="text-sm text-gray-400 line-through mt-1" data-id="element-89">
                    MXN
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2" data-id="element-90">
                  <span className="text-5xl md:text-7xl font-bold text-forest-900" data-id="element-91">
                    $249
                  </span>
                  <span className="text-xl text-gray-500 font-medium mt-4" data-id="element-92">
                    MXN
                  </span>
                </div>
                <p className="text-green-600 font-semibold text-sm mt-3" data-id="element-93">
                  ¡Ahorras $748 MXN hoy!
                </p>
              </div>

              <PrimaryButton className="w-full py-5 text-xl mb-6" data-id="element-94">
                ¡QUIERO DESINFLAMARME AHORA!
              </PrimaryButton>

              <div className="flex flex-col items-center gap-3 text-sm text-gray-500" data-id="element-95">
                <div className="flex items-center gap-2" data-id="element-96">
                  <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-97" />
                  <span data-id="element-98">Acceso inmediato y seguro</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-99">
                  <Clock className="w-5 h-5 text-gold-500" data-id="element-100" />
                  <span data-id="element-101">Comienza tu transformación en 2 minutos</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-dark text-gray-400 py-8 text-center text-sm" data-id="element-102">
        <p data-id="element-103">
          © {new Date().getFullYear()} Protocolo Adiós Glucosa Incógnita. Todos
          los derechos reservados.
        </p>
        <p className="mt-2 text-xs max-w-2xl mx-auto px-6" data-id="element-104">
          Este producto no sustituye el consejo médico profesional. Consulta a
          tu médico antes de comenzar cualquier programa de salud.
        </p>
        <div className="mt-4 flex items-center justify-center gap-6" data-id="element-105">
          <button onClick={() => setLegalModal('privacy')} className="text-gray-500 hover:text-white transition-colors underline underline-offset-2" data-id="element-106">
            Política de Privacidad
          </button>
          <span className="text-gray-600" data-id="element-107">|</span>
          <button onClick={() => setLegalModal('terms')} className="text-gray-500 hover:text-white transition-colors underline underline-offset-2" data-id="element-108">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* LEGAL MODAL */}
      <AnimatePresence data-id="element-109">
        {legalModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setLegalModal(null)} data-id="element-110">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-111">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-112">
                <h3 className="text-xl font-bold text-forest-900" data-id="element-113">
                  {legalModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setLegalModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-114">
                  <X className="w-5 h-5 text-gray-500" data-id="element-115" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-116">
                {legalModal === 'privacy' ? <>
                    <p className="font-semibold text-base text-forest-900" data-id="element-117">
                      Última actualización: Abril 2026
                    </p>
                    <p data-id="element-118">
                      En Protocolo Adiós Glucosa Incógnita, respetamos tu
                      privacidad y nos comprometemos a proteger tus datos
                      personales conforme a la Ley Federal de Protección de
                      Datos Personales en Posesión de los Particulares
                      (LFPDPPP).
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-119">
                      1. Datos que recopilamos
                    </h4>
                    <p data-id="element-120">
                      Recopilamos únicamente los datos necesarios para procesar
                      tu compra: nombre, correo electrónico y datos de pago. No
                      almacenamos información de tarjetas de crédito
                      directamente; estos datos son procesados por plataformas
                      de pago seguras de terceros.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-121">
                      2. Uso de la información
                    </h4>
                    <p data-id="element-122">
                      Tus datos se utilizan exclusivamente para: entregar el
                      producto digital adquirido, enviar comunicaciones
                      relacionadas con tu compra y mejorar nuestros servicios.
                      No vendemos, alquilamos ni compartimos tu información
                      personal con terceros con fines comerciales.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-123">
                      3. Cookies
                    </h4>
                    <p data-id="element-124">
                      Nuestro sitio puede utilizar cookies para mejorar tu
                      experiencia de navegación y analizar el tráfico del sitio.
                      Puedes desactivar las cookies en la configuración de tu
                      navegador.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-125">
                      4. Seguridad
                    </h4>
                    <p data-id="element-126">
                      Implementamos medidas de seguridad técnicas y
                      organizativas para proteger tus datos contra acceso no
                      autorizado, pérdida o alteración.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-127">
                      5. Tus derechos ARCO
                    </h4>
                    <p data-id="element-128">
                      Tienes derecho a Acceder, Rectificar, Cancelar u Oponerte
                      al tratamiento de tus datos personales en cualquier
                      momento.
                    </p>
                  </> : <>
                    <p className="font-semibold text-base text-forest-900" data-id="element-129">
                      Última actualización: Abril 2026
                    </p>
                    <p data-id="element-130">
                      Al adquirir el Protocolo Adiós Glucosa Incógnita, aceptas
                      los siguientes términos y condiciones.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-131">
                      1. Naturaleza del producto
                    </h4>
                    <p data-id="element-132">
                      Este es un producto digital educativo (eBook y guías en
                      formato PDF). No constituye asesoría médica, diagnóstico
                      ni tratamiento. Siempre consulta a un profesional de salud
                      antes de realizar cambios en tu alimentación.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-133">
                      2. Entrega
                    </h4>
                    <p data-id="element-134">
                      El acceso al material digital se entrega de forma
                      inmediata tras confirmar el pago. Recibirás un enlace de
                      descarga en el correo electrónico proporcionado durante la
                      compra.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-135">
                      3. Política de reembolso
                    </h4>
                    <p data-id="element-136">
                      Debido a la naturaleza digital del producto, no se aceptan
                      devoluciones una vez que el material ha sido descargado.
                      Si tienes problemas con la descarga, contáctanos y te
                      ayudaremos a resolverlo.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-137">
                      4. Propiedad intelectual
                    </h4>
                    <p data-id="element-138">
                      Todo el contenido del protocolo está protegido por
                      derechos de autor. Queda prohibida su reproducción,
                      distribución o reventa sin autorización expresa.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-139">
                      5. Resultados
                    </h4>
                    <p data-id="element-140">
                      Los resultados pueden variar de persona a persona. Los
                      testimonios y ejemplos mencionados no garantizan
                      resultados específicos. Tu compromiso y constancia son
                      factores determinantes.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-141">
                      6. Limitación de responsabilidad
                    </h4>
                    <p data-id="element-142">
                      No nos hacemos responsables por el uso inadecuado de la
                      información contenida en el protocolo ni por decisiones de
                      salud tomadas sin supervisión médica profesional.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}